import {
  toWSDateString,
  useModalOldContext,
  WSElement,
  WSElementProps,
  WSTable,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import {
  Currency,
  InvoiceRow,
  PayerRow,
  RecurrenceScheduleDate,
  RecurrenceScheduleDateStatus,
  RecurringDocumentStatus,
  RecurringInvoice
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { queriesInvoiceRows, queriesRecurringInvoices } from "@query";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { WSQueries } from "../../../query/WSQuery";
import { getLocalStartDayByUTC } from "../../../utils/dates";
import { selectorOccurrenceStatusText } from "../../selectors/selectorOccurrenceStatusText";
import { calculateRecurringLineItemsTotal } from "../../utils";
import { buildInvoiceRowIcon } from "../InvoicesTable/buildInvoiceRowIcon";
import { SERIES_INVOICE_ACTIONS_MODAL } from "../SeriesInvoiceActionsModal/SeriesInvoiceActionsModal";

export type SeriesTableItemData = {
  index: number;
  scheduleDate: RecurrenceScheduleDate;
};

type Props = WSElementProps & {
  data: SeriesTableItemData[];
  parentRecurringInvoice: RecurringInvoice;
  payerRow?: PayerRow;
  noActions?: boolean;
};

export const InvoiceSeriesTable: React.FC<Props> = ({
  data,
  parentRecurringInvoice,
  payerRow,
  noActions,
  ...elementProps
}) => {
  const location = useLocation();
  const history = useHistory();
  const recurringInvoicesQuery =
    queriesRecurringInvoices.listRecurringInvoices.useQueryAll();
  const invoiceIds = data
    .map(({ scheduleDate }) => scheduleDate.invoiceId)
    .filter(Boolean) as string[];
  const invoicesQuery = queriesInvoiceRows.searchInvoiceRows.useQueryAll(
    { filter: { id: { anyOf: invoiceIds } } },
    { enabled: invoiceIds.length > 0 }
  );
  // No `scheduleDate.invoiceId` in this series yet — the query above stays
  // disabled (never succeeds on its own), so short-circuit the gate below.
  const invoicesQueryForGate =
    invoiceIds.length === 0
      ? { ...invoicesQuery, isSuccess: true, flatData: [] as InvoiceRow[] }
      : invoicesQuery;
  const { openModal } = useModalOldContext();

  const payerName = payerRow ? selectorSearchRowName(payerRow) : "";
  const payerSecondaryName = payerRow?.email ?? "";
  const isCancelledSeries =
    parentRecurringInvoice.status === RecurringDocumentStatus.CANCELLED;

  return (
    <WSElement {...elementProps}>
      <WSQueries
        queries={{
          recurringInvoicesQuery,
          invoicesQuery: invoicesQueryForGate
        }}
      >
        {({
          recurringInvoicesQuery: { flatData: recurringInvoices },
          invoicesQuery: { flatData: invoices }
        }) => {
          const findOverride = (overrideId?: string) =>
            overrideId
              ? recurringInvoices.find(item => item.id === overrideId)
              : undefined;

          const getScheduleDateStatusText = (
            scheduleDate: RecurrenceScheduleDate
          ): string =>
            selectorOccurrenceStatusText({
              scheduleDate,
              seriesStatus: parentRecurringInvoice.status,
              override: findOverride(scheduleDate.invoiceTemplateId)
            });

          return (
            <WSTable
              tableData={data.map(({ index, scheduleDate }) => {
                const invoice = scheduleDate.invoiceId
                  ? invoices.find(i => i.id === scheduleDate.invoiceId)
                  : undefined;

                return {
                  id: String(index),
                  data: {
                    scheduleDate,
                    invoice,
                    override: findOverride(scheduleDate.invoiceTemplateId)
                  }
                };
              })}
              onRowClick={
                noActions
                  ? undefined
                  : ({ id, data }) => {
                      if (data.invoice) {
                        history.push({
                          pathname: `${location.pathname}/invoice/${data.invoice.id}`,
                          state: {
                            backPath: location.pathname
                          },
                          search: location.search
                        });
                      } else {
                        openModal(SERIES_INVOICE_ACTIONS_MODAL, {
                          parentInvoiceTemplateId: parentRecurringInvoice.id,
                          scheduleDateIndex: Number(id)
                        });
                      }
                    }
              }
              columns={[
                {
                  config: {
                    gridTemplateSizeMax: "0.7fr"
                  },
                  renderFunction: ({ data: { scheduleDate, invoice } }) => (
                    <WSTableCell
                      avatar={{
                        type: "icon",
                        ...(isCancelledSeries &&
                        scheduleDate.status !==
                          RecurrenceScheduleDateStatus.COMPLETED
                          ? {
                              icon: "exit-circle",
                              colorBackground: "gray50",
                              color: "gray500"
                            }
                          : scheduleDate.status ===
                              RecurrenceScheduleDateStatus.SKIPPED
                            ? {
                                icon: "skip-circle",
                                colorBackground: "gray50",
                                color: "gray500"
                              }
                            : invoice
                              ? buildInvoiceRowIcon(invoice)
                              : {
                                  icon: "calendar",
                                  colorBackground: "gray50",
                                  color: "gray500"
                                })
                      }}
                      text={payerName}
                      secondaryText={payerSecondaryName}
                    />
                  )
                },
                {
                  config: {
                    gridTemplateSizeMax: "0.6fr",
                    justify: "end"
                  },
                  renderFunction: ({ data }) => {
                    return (
                      <WSTableCell
                        text={formatMoney(
                          data.invoice
                            ? (data.invoice.amount ?? 0)
                            : calculateRecurringLineItemsTotal(
                                data.override?.invoiceTemplate?.lineItems ??
                                  parentRecurringInvoice.invoiceTemplate
                                    ?.lineItems
                              ),
                          (data.override?.currency ??
                            parentRecurringInvoice.currency) as Currency
                        )}
                        secondaryText={`${getScheduleDateStatusText(
                          data.scheduleDate
                        )} ${toWSDateString(
                          getLocalStartDayByUTC(data.scheduleDate.date),
                          "monthDate"
                        )}`}
                      />
                    );
                  }
                }
              ]}
            />
          );
        }}
      </WSQueries>
    </WSElement>
  );
};
